import { DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, MessageBar, MessageBarType, PrimaryButton, Stack } from '@fluentui/react';
import React, { useMemo, useState } from 'react';
import BlockUi from 'react-block-ui';
import ReactDOM from 'react-dom';
import { Provider as ProviderContext, useSelector } from 'react-redux';
import { changeServiceCode } from '../../../api/visits';
import { RootState } from '../../../redux/types';
import { Visit } from '../../../redux/visits/types';
import { store } from '../../../redux/store';
import { age } from '../../../util/Date';

interface Props {
  visit: Visit;
  onSuccess: () => void;
}

export class ChangeServiceOfferingDialog {
  public static show(options: Props) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(
      <ProviderContext store={store}>
        <ChangeServiceOfferingInternal {...options} close={close} />
      </ProviderContext>,
      dialogNode
    );
  }
}

const ChangeServiceOfferingInternal: React.FC<Props & { close: () => void }> = ({ visit, onSuccess, close }) => {
  const [serviceCode, setServiceCode] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const services = useSelector((state: RootState) => state.serviceOffering.services.data || []);
  const patientAge = age(visit.patientDob);

  const reasonsForDropdown = useMemo(() => {
    return services.filter((s) => s.code !== visit.visitServiceCode && patientAge >= s.ageMinimum && patientAge <= s.ageMaximum).map((s) => ({ key: s.code, text: s.name }));
  }, [services, visit.visitServiceCode, patientAge]);

  const onClickOk = () => {
    setIsSaving(true);
    changeServiceCode(visit.visitCode, serviceCode)
      .then((ok) => {
        if (ok) {
          close();
          onSuccess();
        }
      })
      .catch((error) => {
        setErrorMessage(error.message);
        setIsSaving(false);
      });
  };

  return (
    <Dialog hidden={false} dialogContentProps={{ type: DialogType.largeHeader, title: 'Change service offering: ' + visit.visitCode }} onDismiss={close}>
      <BlockUi tag="div" blocking={isSaving}>
        <Stack tokens={{ childrenGap: 15 }}>
          <Dropdown
            placeholder="(Select a new service type)"
            label="Target Service Type:"
            options={reasonsForDropdown}
            selectedKey={serviceCode}
            onChange={(_, option) => option && setServiceCode(option.key as string)}
          />

          {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}

          <DialogFooter>
            <PrimaryButton onClick={onClickOk} disabled={!serviceCode} data-tid="btn_changeService_ok">
              Ok
            </PrimaryButton>
            <DefaultButton onClick={close} data-tid="btn_changeService_cancel">
              Cancel
            </DefaultButton>
          </DialogFooter>
        </Stack>
      </BlockUi>
    </Dialog>
  );
};
